import { type ReactNode } from "react";
import { CaretDoubleRightIcon } from "@phosphor-icons/react/CaretDoubleRight";
import { XIcon } from "@phosphor-icons/react/X";
import { Banner } from "../../Display/Banner";
import { ControlBar } from "../../PageLayout/ControlBar";
import { Menu } from "../../Menu/Menu";
import { DialogHeaderProvider } from "./DialogHeaderContext";
import { PortalContextReset } from "../../Controls/Internal/PortalContextReset";
import { useScrollFade } from "./useScrollFade";

// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
export function DialogContent_({
  titleNode,
  descriptionNode,
  warning,
  headerFilters,
  headerActions,
  menu,
  filterBar,
  children,
  finalFooter,
  onPeek,
  onClose,
  okLoading,
}: {
  /**
   * The rendered title element. Modal mode passes a Radix `Dialog.Title`
   * with `asChild` wrapping the visible h2, so the visible heading is
   * also the accessible name — no sr-only duplicate. Inline mode passes
   * a plain h2.
   */
  titleNode?: ReactNode;
  /**
   * The rendered description element, same deal as `titleNode`: modal mode
   * wraps the visible `<p>` in Radix's `Dialog.Description` with `asChild`,
   * so the visible line IS the accessible description. Rendering an sr-only
   * copy alongside it would read the sentence to a screen reader twice.
   */
  descriptionNode?: ReactNode;
  warning?: string | undefined;
  headerFilters?: ReactNode;
  headerActions?: ReactNode;
  menu?: ReactNode;
  filterBar?: ReactNode;
  children: ReactNode;
  finalFooter?: ReactNode;
  onPeek?: (() => void) | undefined;
  onClose?: (() => void) | undefined;
  okLoading?: boolean | undefined;
}) {
  const hasCloseButtons = !!onPeek && !!onClose;
  const hasRightSide =
    !!headerActions || !!menu || !!headerFilters || hasCloseButtons;
  const hasHeader = !!(
    titleNode ||
    descriptionNode ||
    hasRightSide ||
    filterBar ||
    warning
  );
  // Padding lives on an inner wrapper rather than the scroll container so
  // the sticky fade can anchor flush to the bottom of the scrollport — with
  // pb-6 on the scroll container, sticky `bottom: 0` sits 24px above the
  // visual bottom because the padding box bottom is the scrollport edge.
  // `flex flex-col min-h-full` lets a child opt into filling the dialog body
  // height by setting `flex-1` (or Stack `grow`). Without it the wrapper hugs
  // its content and any `flex-1` child has no defined parent height to grow
  // against. Short content still sits at the top; the only visual change is
  // that the wrapper extends to the bottom of the scroll viewport, which is
  // invisible until something inside actually opts in.
  const bodyPaddingClass = `flex min-h-full flex-col px-8 pb-8 ${hasHeader ? "" : "pt-8"}`;
  const { scrollRef, showFade } = useScrollFade();
  return (
    // A dialog is its own layout root: opened from a table row (or any other
    // adapting surface), its portaled content would otherwise inherit that
    // surface's contexts and e.g. collapse the footer's Save button to
    // icon-only. Reset here — shared by modal AND inline (Storybook) modes so
    // snapshots match runtime. The header's own DialogHeaderProvider below is
    // deeper than the reset, so header actions keep their icon-action style.
    <PortalContextReset>
      {/* ── Fixed header ─────────────────────────────────────── */}
      {hasHeader && (
        <div className="shrink-0 px-8 pt-7 pb-5">
          {(titleNode || descriptionNode || hasRightSide) && (
            <div className="flex items-start justify-between gap-4">
              {(titleNode || descriptionNode) && (
                <div className="min-w-0">
                  {titleNode}
                  {descriptionNode}
                </div>
              )}
              {hasRightSide && (
                <div className="ml-auto flex shrink-0 items-center gap-2">
                  <ControlBar
                    filters={headerFilters}
                    actions={
                      headerActions && (
                        <DialogHeaderProvider value={true}>
                          {headerActions}
                        </DialogHeaderProvider>
                      )
                    }
                  />
                  {menu && (
                    <Menu variant="dialog" data-testid="dialog-menu">
                      {menu}
                    </Menu>
                  )}
                  {hasCloseButtons && (
                    <>
                      <button
                        type="button"
                        data-testid="dialog-peek"
                        disabled={okLoading}
                        onClick={onPeek}
                        className="inline-flex items-center rounded-md px-2 py-1 text-content-faint transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
                      >
                        <CaretDoubleRightIcon
                          className="h-3.5 w-3.5 shrink-0"
                          weight="bold"
                        />
                      </button>
                      <button
                        type="button"
                        data-testid="dialog-close"
                        disabled={okLoading}
                        onClick={onClose}
                        className="inline-flex items-center rounded-md px-2 py-1 text-content-faint transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
                      >
                        <XIcon className="h-3.5 w-3.5 shrink-0" />
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {filterBar && (
            <div className="mt-3 border-t border-border pt-3">
              <ControlBar filters={filterBar} />
            </div>
          )}
          {warning && (
            <div className="mt-4">
              <Banner variant="warning">{warning}</Banner>
            </div>
          )}
        </div>
      )}
      {/* ── Scrollable body ───────────────────────────────────
        Single scrollable element. min-h-0 + flex-auto lets the body shrink
        when the parent (Radix.Content) hits its max-h-[94dvh] cap and grow
        to fit content otherwise. Avoids the wrapper-with-h-full pattern,
        which produced 0-height bodies on flex-1 (basis 0%) and brittle
        intrinsic-vs-percent height resolution on flex-auto.
        Padding goes on the children-wrapper, not the scroll container, so
        the sticky fade can anchor flush to the visual bottom.
      */}
      <div
        ref={scrollRef}
        className="relative min-h-0 flex-auto overflow-y-auto"
      >
        <div className={bodyPaddingClass}>{children}</div>
        {showFade && (
          // Sticky bottom fade indicator. -mt-12 cancels the 48px the
          // element would otherwise occupy in flow, so the fade overlays
          // the last bit of content instead of pushing the layout down.
          <div className="pointer-events-none sticky right-0 bottom-0 left-0 -mt-12 h-12 rounded-b-2xl bg-gradient-to-t from-card to-transparent" />
        )}
      </div>
      {finalFooter && (
        <div className="flex shrink-0 items-center justify-end gap-3 px-8 py-5">
          {finalFooter}
        </div>
      )}
    </PortalContextReset>
  );
}
